import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type SkuMatchDTO } from "../../api";
import { crudEditor, selectField, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * The retailer-SKU → (artwork, product) lookup that order dispatch consults
 * before it can build a production job. A SKU with no row here shows up as
 * "No match" in the dispatch dialog and has to be matched by hand.
 */
export function SkuMatchPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.CONFIG_SKU_MATCH);
  const title = t("navigation.item.sku-match", "Sku match");

  const { data, isLoading, error } = useQuery({
    queryKey: ["sku-match"],
    queryFn: () => api.skuMatches.findAll(),
    enabled: allowed,
  });

  const artworks = useQuery({
    queryKey: ["artwork", "core"],
    queryFn: () => api.artworkCores.findAll(),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });

  const products = useQuery({
    queryKey: ["product"],
    queryFn: () => api.products.findAll(),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });

  const rows = data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["sku-match"] });

  const artworkById = useMemo(
    () => new Map((artworks.data ?? []).map((entry) => [entry.id, entry])),
    [artworks.data],
  );
  const productById = useMemo(
    () => new Map((products.data ?? []).map((entry) => [entry.id, entry])),
    [products.data],
  );

  const cols = useMemo(() => {
    const c = defineColumns<SkuMatchDTO>();
    return [
      c.text("sku", (row) => row.sku, { headerKey: "column.sku", priority: 1 }),
      c.text(
        "artwork",
        (row) => artworkById.get(row.artwork_id)?.metadata.code ?? "not found",
        { headerKey: "column.artwork", filter: "select", priority: 2 },
      ),
      c.text("product", (row) => productById.get(row.product_id)?.code ?? "not found", {
        headerKey: "column.product",
        filter: "select",
        priority: 3,
      }),
    ];
  }, [artworkById, productById]);

  const editor = useMemo(
    () =>
      crudEditor<SkuMatchDTO>({
        client: api.skuMatches,
        refresh,
        t,
        fields: {
          sku: textField(
            (row) => row.sku,
            (row, value) => ({ ...row, sku: value }),
            true,
          ),
          artwork: selectField(
            (row) => (row.artwork_id ? String(row.artwork_id) : ""),
            (row, value) => ({ ...row, artwork_id: Number(value) }),
            [...artworkById.values()]
              .map((entry) => ({ value: String(entry.id), label: entry.metadata.code }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          ),
          product: selectField(
            (row) => (row.product_id ? String(row.product_id) : ""),
            (row, value) => ({ ...row, product_id: Number(value) }),
            [...productById.values()]
              .map((entry) => ({ value: String(entry.id), label: entry.code }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          ),
        },
        newRow: () => ({ id: 0, sku: "", artwork_id: 0, product_id: 0 }),
        validate: (row) => {
          const errors: Record<string, string> = {};
          if (!row.sku.trim()) errors.sku = t("form.validation.field.required", "Value is required");
          else if (rows.some((entry) => entry.id !== row.id && entry.sku === row.sku)) {
            errors.sku = t("form.validation.field.unique", "Value already exists");
          }
          if (!row.artwork_id) errors.artwork = t("form.validation.field.required", "Value is required");
          if (!row.product_id) errors.product = t("form.validation.field.required", "Value is required");
          return Object.keys(errors).length > 0 ? errors : null;
        },
      }),
    [api, artworkById, productById, rows, t],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<SkuMatchDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading || artworks.isLoading || products.isLoading}
        editor={editor}
      />
    </>
  );
}
